/**
 * Labels for a shift-day request's period, shared by everything that shows one:
 * the approver's queue, the attendance card and the approval history. They drifted
 * apart once already -- one of them was still reading a `date` field the API had
 * replaced with a range, and showed nothing at all.
 */

/** "22 Oct" — enough to place the day, short enough for a collapsed row. */
export const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });

/** "22 Oct 2026" — for a history line, where the year is no longer obvious. */
export const dayWithYear = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "22 Oct", or "22 Oct – 24 Oct" when the ask spans more than one day. */
export const shiftPeriodLabel = (startDate: string, endDate: string) =>
  startDate === endDate ? shortDay(startDate) : `${shortDay(startDate)} – ${shortDay(endDate)}`;

/** The same with years, for lines that stand on their own. */
export const shiftPeriodWithYear = (startDate: string, endDate: string) =>
  startDate === endDate ? dayWithYear(startDate) : `${shortDay(startDate)} – ${dayWithYear(endDate)}`;

/** Whole days in the period, counted inclusively — "3 days", not "2". */
export function shiftPeriodDays(startDate: string, endDate: string): number {
  const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

/** Hours between two HH:MM times on the same day. */
export const shiftWindowHours = (from: string, to: string) =>
  (Number(to.slice(0, 2)) * 60 + Number(to.slice(3)) - Number(from.slice(0, 2)) * 60 - Number(from.slice(3))) / 60;
